/**
 * Users Routes (Hono + OpenAPI)
 *
 * Custom endpoints layered on top of auto-mounted CRUD:
 * - GDPR: /me, /me/export, /me/consent, DELETE /me
 * - Multi-role: /:userId/roles (GET/POST/PUT, DELETE /:userId/roles/:roleId)
 * - Self-delete guard: DELETE /:id blocks deleting yourself (wins over auto)
 *
 * CRUD for users + roles is auto-mounted by setupModuleRoutes (step 2c).
 */

import { OpenAPIHono, createRoute, z } from '@hono/zod-openapi'
import type { MiddlewareHandler } from 'hono'
import type { ModuleContext } from '@gzl10/nexus-sdk'
import type { NexusEnv, OpenApiHandler } from '../../http/types.js'
import { requireAbility } from '../../http/middleware/ability.middleware.js'
import type { Subjects } from '@kernel/abilities/ability.types.js'
import { createUsersController } from './users.controller.js'

// GDPR Consent schema
export const consentSchema = z.object({
  version: z.string().min(1).max(20),
  marketingOptIn: z.boolean().default(false)
})

export function createUsersRoutes(ctx: ModuleContext) {
  const app = new OpenAPIHono<NexusEnv>()
  const controller = createUsersController(ctx)

  const authMw = ctx.core.middleware['auth']
  if (!authMw) {
    throw new Error('Auth middleware is required for users routes')
  }
  const auth = authMw as unknown as MiddlewareHandler<NexusEnv>

  const canDeleteUser = requireAbility(
    'delete',
    'User' as Subjects
  ) as unknown as MiddlewareHandler<NexusEnv>

  // Runs before CASL so the specific "eliminarte" message surfaces for self-delete attempts.
  const selfDeleteGuard: MiddlewareHandler<NexusEnv> = async (c, next) => {
    const id = c.req.param('id')
    const actorId = c.get('user')?.id
    if (actorId && actorId === id) {
      throw new ctx.core.errors.ForbiddenError('No puedes eliminarte a ti mismo')
    }
    await next()
  }

  const anyObject = z.object({}).passthrough()

  const userIdParams = z.object({ userId: z.string() })
  const userRoleParams = z.object({ userId: z.string(), roleId: z.string() })
  const idParams = z.object({ id: z.string() })

  // --- GET /me ---
  const meRoute = createRoute({
    method: 'get',
    path: '/me',
    tags: ['Users'],
    summary: 'Get current authenticated user',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    responses: {
      200: { description: 'Current user', content: { 'application/json': { schema: anyObject } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(meRoute, controller.getMe as OpenApiHandler)

  // --- GET /me/export (GDPR Art. 15) ---
  const exportMeRoute = createRoute({
    method: 'get',
    path: '/me/export',
    tags: ['Users'],
    summary: 'Export all personal data (GDPR Right of Access)',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    responses: {
      200: { description: 'User data export', content: { 'application/json': { schema: anyObject } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(exportMeRoute, controller.exportMe as OpenApiHandler)

  // --- DELETE /me (GDPR Art. 17) ---
  const deleteMeRoute = createRoute({
    method: 'delete',
    path: '/me',
    tags: ['Users'],
    summary: 'Delete own account (GDPR Right to Erasure)',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    responses: {
      200: { description: 'Account deleted', content: { 'application/json': { schema: anyObject } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(deleteMeRoute, controller.deleteMe as OpenApiHandler)

  // --- POST /me/consent ---
  const consentRoute = createRoute({
    method: 'post',
    path: '/me/consent',
    tags: ['Users'],
    summary: 'Record GDPR consent',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    request: {
      body: {
        content: { 'application/json': { schema: consentSchema } }
      }
    },
    responses: {
      200: { description: 'Consent recorded', content: { 'application/json': { schema: anyObject } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(consentRoute, controller.recordConsent as OpenApiHandler)

  // --- GET /:userId/roles ---
  const getUserRolesRoute = createRoute({
    method: 'get',
    path: '/{userId}/roles',
    tags: ['Users'],
    summary: 'Get roles for a user',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    request: { params: userIdParams },
    responses: {
      200: { description: 'Roles', content: { 'application/json': { schema: z.array(anyObject) } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(getUserRolesRoute, controller.getUserRoles as OpenApiHandler)

  // --- POST /:userId/roles (add single role) ---
  const assignRoleRoute = createRoute({
    method: 'post',
    path: '/{userId}/roles',
    tags: ['Users'],
    summary: 'Assign a role to a user',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    request: {
      params: userIdParams,
      body: { content: { 'application/json': { schema: z.object({ roleId: z.string().min(1) }) } } }
    },
    responses: {
      200: { description: 'Roles after assign', content: { 'application/json': { schema: z.array(anyObject) } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(assignRoleRoute, controller.assignRole as OpenApiHandler)

  // --- PUT /:userId/roles (replace all) ---
  const setRolesRoute = createRoute({
    method: 'put',
    path: '/{userId}/roles',
    tags: ['Users'],
    summary: 'Replace all roles for a user',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    request: {
      params: userIdParams,
      body: { content: { 'application/json': { schema: z.object({ roleIds: z.array(z.string()) }) } } }
    },
    responses: {
      200: { description: 'Roles after replace', content: { 'application/json': { schema: z.array(anyObject) } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(setRolesRoute, controller.setRoles as OpenApiHandler)

  // --- DELETE /:userId/roles/:roleId ---
  const removeRoleRoute = createRoute({
    method: 'delete',
    path: '/{userId}/roles/{roleId}',
    tags: ['Users'],
    summary: 'Remove a role from a user',
    security: [{ bearerAuth: [] }],
    middleware: [auth] as const,
    request: { params: userRoleParams },
    responses: {
      200: { description: 'Success', content: { 'application/json': { schema: anyObject } } },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(removeRoleRoute, controller.removeRole as OpenApiHandler)

  // --- DELETE /:id (self-delete guard; takes precedence over auto-mounted CRUD) ---
  const deleteUserRoute = createRoute({
    method: 'delete',
    path: '/{id}',
    tags: ['Users'],
    summary: 'Delete a user (blocked if self)',
    security: [{ bearerAuth: [] }],
    middleware: [auth, selfDeleteGuard, canDeleteUser] as const,
    request: { params: idParams },
    responses: {
      204: { description: 'User deleted' },
      401: { description: 'Unauthorized', content: { 'application/json': { schema: anyObject } } },
      403: { description: 'Forbidden', content: { 'application/json': { schema: anyObject } } }
    }
  })
  app.openapi(deleteUserRoute, controller.deleteUser as OpenApiHandler)

  return app
}
